import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { CareCategories, ProviderTypes } from '../../common/Constants';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { getFeatureFlag } from '../../common/Utils';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { useTypeaheadStore } from '../../store/useTypeaheadStore';
import { TypeaheadState } from '../../store/useTypeaheadStore/typeaheadStore';
import { returnSuppressFlag } from '../../utils/featureSuppress';
import { useIsNiceMember } from '../useIsNiceMember';
import { useLagoon } from '../useLagoon';

export const useSmartChoice = (ffAndSupressionFFCheckOnly = false): boolean => {
  const featureFlags: [{ key: string; active: boolean }] =
    useLagoon('feature-flags')();
  const enablePSXProviderRecommendations = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PSX_PROVIDER_RECOMMENDATIONS
  );
  const enableSmartChoiceForFacilities = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PSX_FACILITIES_RECOMMENDATIONS
  );
  const chipStore = useChipStore(useShallow((state: ChipState) => state));

  // for USP Nice and ENI Nice disable smart choice
  const isNiceMember = useIsNiceMember();
  if (isNiceMember) {
    return false;
  }

  const { chipValue: chipValueFromStore } = chipStore;
  const queryString = window.location.search;
  const searchParams = new URLSearchParams(queryString);
  const chipValueFromParams = searchParams.get('chipValue');
  const sectionType = searchParams.get('sectionType');
  const chipValue = chipValueFromParams || chipValueFromStore;
  const primaryCareFromDashboard = CareCategories.PRIMARY_CARE.replace(' ', '');

  // Build eligible categories list - include FACILITIES only if feature flag is enabled
  const eligibleCategories = [
    CareCategories.MEDICAL_SPECIALISTS,
    CareCategories.PRIMARY_CARE,
    primaryCareFromDashboard,
  ];
  if (enableSmartChoiceForFacilities) {
    eligibleCategories.push(CareCategories.FACILITIES);
  }

  const isSmartChoiceEligibleCategory = eligibleCategories.includes(
    chipValue as CareCategories
  );

  const isNameSearch = searchParams.get('searchType') === 'name';
  const isNotSpecialitySearch =
    searchParams.get('searchBySpeciality') === 'false';

  const { providerType = '' } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      providerType: state.typeaheadSearchStore.providerType,
    }))
  );
  const isSmartChoiceSuppressed = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.SMART_CHOICE
  );

  const { notGroupType, notFacilityType } = useMemo(
    () => ({
      notGroupType:
        sectionType?.toLowerCase() === ProviderTypes.GROUP?.toLowerCase(),
      notFacilityType:
        sectionType?.toLowerCase() === ProviderTypes.FACILITY?.toLowerCase(),
    }),
    [sectionType]
  );

  if (isNotSpecialitySearch || isNameSearch) {
    return false;
  }
  const baseCondition =
    !isSmartChoiceSuppressed && enablePSXProviderRecommendations;
  if (ffAndSupressionFFCheckOnly) {
    return baseCondition;
  }

  // Base conditions that must be met regardless of section type
  if (!baseCondition || !isSmartChoiceEligibleCategory || notGroupType) {
    return false;
  }

  // Provider type validation
  // Allow PRACTITIONER or undefined for all Smart Choice categories
  // Allow ORGANIZATION only when on Facilities chip with facilities flag ON
  const isValidProviderType =
    !providerType ||
    providerType === ProviderTypes.PRACTITIONER ||
    (providerType === ProviderTypes.ORGANIZATION &&
      enableSmartChoiceForFacilities &&
      chipValue === CareCategories.FACILITIES);

  if (!isValidProviderType) {
    return false;
  }

  // Handle facility section type specifically
  // Smart Choice in facility sections is only enabled when:
  // 1. Feature flag is ON, AND
  // 2. User is on the Facilities chip
  if (notFacilityType) {
    return (
      enableSmartChoiceForFacilities && chipValue === CareCategories.FACILITIES
    );
  }

  // For all other section types (or no section type), Smart Choice is enabled
  // if base conditions are met (already checked above)
  return true;
};
